import nextCookie from 'next-cookies';
import {
  SpecialClassesResponse,
  SubCategoriesResponse,
  LiveNowResponse,
  SheldonLibraryResponse
} from '@api/fetchers/prime/types';
import { fetchGoalInfo } from '@api/fetchers/goal';
import { getKey as getTopologyKey } from '@api/hooks/topology/useTopology';
import { getKey as getSheldonKey } from '@api/hooks/prime/usePrimeLibrary';
import {
  fetchSpecialClasses,
  fetchPrimeSubCategories,
  fetchLiveNow,
  fetchSheldonLibrary
} from '@api/fetchers/prime';

export interface PrimePageProps {
  goal: any;
  specialClasses: SpecialClassesResponse | null;
  subCategories: SubCategoriesResponse;
  liveNow: LiveNowResponse | null;
  sheldon: SheldonLibraryResponse | null;
  fallback: Record<string, any>;
}

const getGoalUIDFromQuery = (query: Record<string, any>) => {
  const goalUID = query?.goalUID;
  return Array.isArray(goalUID) ? goalUID[0] : goalUID;
};

const getAccessTokenFromCtx = (ctx: any) => {
  const cookieAccessToken = nextCookie(ctx)?.accessToken;
  return (
    ctx?.cookies?.accessToken ||
    cookieAccessToken ||
    ctx?.store?.getState?.()?.auth?.accessToken ||
    null
  );
};

const EMPTY_PRIME_RESULT = {
  goal: null,
  specialClasses: null,
  subCategories: [],
  liveNow: null,
  sheldon: null,
  fallback: {}
};

export const primeGetInitialProps = async (ctx: any) => {
  try {
    const goalUID = getGoalUIDFromQuery(ctx?.query || {});
    const accessToken = getAccessTokenFromCtx(ctx);

    if (!goalUID) return EMPTY_PRIME_RESULT;

    const goal: any = await fetchGoalInfo(goalUID);
    if (!goal?.uid) return EMPTY_PRIME_RESULT;

    const [specialRes, subCatRes, liveRes] = await Promise.allSettled([
      fetchSpecialClasses(goalUID, accessToken),
      fetchPrimeSubCategories(goalUID, accessToken),
      fetchLiveNow(goalUID, accessToken)
    ]);

    const specialClasses =
      specialRes.status === 'fulfilled' ? specialRes.value : null;
    const subCategories =
      subCatRes.status === 'fulfilled' ? subCatRes.value : [];
    const liveNow = liveRes.status === 'fulfilled' ? liveRes.value : null;
    const defaultSubCat = subCategories?.[0]?.uid;
    let sheldon = null;
    if (defaultSubCat) {
      try {
        sheldon = await fetchSheldonLibrary(defaultSubCat, accessToken);
      } catch {
        sheldon = null;
      }
    }

    return {
      goal,
      specialClasses,
      subCategories,
      liveNow,
      sheldon,
      fallback: {
        [getTopologyKey(goal.uid)]: goal,
        ...(sheldon ? { [getSheldonKey(defaultSubCat)]: sheldon } : {})
      }
    };
  } catch (err) {
    return EMPTY_PRIME_RESULT;
  }
};
